/**
 * Planning Board Query Hooks (Epic C-3 — Planbord & indelen, Story C-3.3)
 *
 * Eigen bestand, zelfde query-keys-factory + invalidatie-conventie als
 * `useQualifications.ts`/`usePlanningTasks.ts` (AD-9/AD-18). Consumeert
 * `planningApi.getBoard`/`upsertAssignment`/`deleteAssignment` (C-3.1/C-3.2).
 *
 * Optimistische UI (AC 4) leeft bewust in `PlanningCell.tsx` zelf (lokale
 * override-state + rollback-op-fout), NIET hier via React Query
 * `onMutate`-cache-patching — zelfde afweging als `QualificationCheckbox.tsx`
 * (Story C-2.2): de mutatie-hooks hieronder invalideren simpelweg de board-
 * cache na een geslaagde mutatie, de cel toont intussen zijn eigen optimisme.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { planningApi } from '../../../lib/planningApi';
import { occupancyKeys } from './useOccupancy';
import { conflictsKeys } from './useConflicts';
import type { PlanningDayPart, UpsertAssignmentItem } from '@functieloonhuis/shared';

export const planningBoardKeys = {
  all: ['planningBoard'] as const,
  board: (van: string, tot: string, departmentId?: number) => [...planningBoardKeys.all, 'board', van, tot, departmentId ?? null] as const,
};

export function usePlanningBoard(van: string, tot: string, departmentId?: number) {
  return useQuery({
    queryKey: planningBoardKeys.board(van, tot, departmentId),
    queryFn: () => planningApi.getBoard(van, tot, departmentId),
    enabled: !!van && !!tot,
  });
}

/**
 * Story C-4.1/C-4.2 (AD-18): een indeling-mutatie kan de bezetting- en
 * conflicten-projecties ook laten wijzigen (bv. een indeling die het
 * onderliggende conflict oplost) — invalideer daarom alle drie de
 * `planningService`-lezingen, niet alleen het board zelf.
 */
function invalidatePlanningReads(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: planningBoardKeys.all });
  queryClient.invalidateQueries({ queryKey: occupancyKeys.all });
  queryClient.invalidateQueries({ queryKey: conflictsKeys.all });
}

export function useUpsertAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: UpsertAssignmentItem[]) => planningApi.upsertAssignment(items),
    onSuccess: () => {
      invalidatePlanningReads(queryClient);
    },
  });
}

export interface DeleteAssignmentInput {
  employeeId: number;
  date: string;
  dayPart: PlanningDayPart;
}

export function useDeleteAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DeleteAssignmentInput) => planningApi.deleteAssignment(input.employeeId, input.date, input.dayPart),
    onSuccess: () => {
      invalidatePlanningReads(queryClient);
    },
  });
}
